import {
  useReactTable,
  ColumnDef,
  SortingState,
  getCoreRowModel,
  flexRender,
  PaginationState,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkTrackingDto, TrackedLinkItemDto } from '@/Domain/api/Analytics/dto/LinkTracking.dto';
import { EmptyTableRow } from '@/Domain/components/Insights/LinkTracking/TableRow/EmptyTableRow';
import { ErrorTableRow } from '@/Domain/components/Insights/LinkTracking/TableRow/ErrorTableRow';
import { InactiveLinkTrackingTableRow } from '@/Domain/components/Insights/LinkTracking/TableRow/InactiveLinkTrackingTableRow';
import { LoadingTableRow } from '@/Domain/components/Insights/LinkTracking/TableRow/LoadingTableRow';
import { CardContent } from '@/Domain/components/Shared/Card/Card';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationButton,
  PaginationNext,
  PaginationPrevious,
} from '@/Domain/components/Shared/Pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Domain/components/Shared/Table/Table';
import { SortAscIcon } from '@/Domain/components/icons/SortAsc.icon';
import { SortDescIcon } from '@/Domain/components/icons/SortDesc.icon';
import { PaginationHelper } from '@/Domain/helper/Pagination.helper';

type Props = {
  isError: boolean;
  isLoading: boolean;
  isLinkTrackingActive: boolean;
  data: LinkTrackingDto | undefined;
  sorting: SortingState;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  setSorting: Dispatch<SetStateAction<SortingState>>;
};

export const LinkTrackingTable = ({
  isError,
  isLoading,
  isLinkTrackingActive,
  data,
  sorting,
  setSorting,
  pagination,
  setPagination,
}: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.link_tracking' });

  const totalPages = Math.ceil((data?.count ?? 0) / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  const pages = useMemo(
    () => PaginationHelper.getRange(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const columns: ColumnDef<TrackedLinkItemDto>[] = [
    {
      accessorKey: 'short_format_url',
      header: () => t('url'),
      enableSorting: false,
      minSize: 350,
      cell: ({ row }) => {
        const shortUrl = row.original.short_format_url;
        const fullUrl = row.original.full_url;
        return (
          <a
            href={fullUrl}
            target="_blank"
            rel="noreferrer"
            className="truncate inline-block max-w-xs"
          >
            {shortUrl}
          </a>
        );
      },
    },
    {
      accessorKey: 'generation_count',
      header: () => t('sent'),
      cell: (info) => info.getValue<number>(),
      enableSorting: true,
      meta: { align: 'right' },
    },
    {
      accessorKey: 'click_count',
      header: () => t('clicked'),
      cell: (info) => info.getValue<number>(),
      enableSorting: true,
      meta: { align: 'right' },
    },
    {
      id: 'conversion',
      header: () => t('conversion'),
      accessorFn: (row) =>
        row.generation_count ? (row.click_count / row.generation_count) * 100 : 0,
      cell: (info) => `${info.getValue<number>().toFixed(0)}%`,
      enableSorting: false,
      meta: { align: 'right' },
    },
  ];

  const table = useReactTable({
    data: data?.items || [],
    rowCount: data?.count || 0,
    columns,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    state: {
      sorting,
      pagination,
    },
  });

  if (isError) return <ErrorTableRow />;
  if (isLoading) return <LoadingTableRow />;
  if (!isLinkTrackingActive && !data?.items.length) return <InactiveLinkTrackingTableRow />;
  if (!data?.items.length) return <EmptyTableRow />;

  return (
    <CardContent className="pt-0">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={classNames('', {
                    'cursor-pointer': header.column.columnDef.enableSorting,
                  })}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div
                    className={classNames('group flex items-center gap-2', {
                      'justify-end': header.column.columnDef.meta?.align === 'right',
                    })}
                  >
                    <span
                      className={classNames('', {
                        hidden: !header.column.columnDef.enableSorting,
                      })}
                    >
                      {{
                        asc: <SortAscIcon className="size-4.5" />,
                        desc: <SortDescIcon className="size-4.5" />,
                      }[header.column.getIsSorted() as string] || (
                        <SortDescIcon className="size-4.5 opacity-0 group-hover:!opacity-100 text-foreground-subtle ease-in-out transition-opacity duration-300" />
                      )}
                    </span>
                    <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    width: cell.column.getSize() || 'auto',
                  }}
                  className={
                    cell.column.columnDef.meta?.align === 'right'
                      ? 'text-right font-medium'
                      : 'text-left'
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex items-center justify-center pt-4 gap-3">
        <Pagination>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {t('previous')}
            </PaginationPrevious>
          </PaginationItem>

          <PaginationContent>
            {pages.map((page, idx) =>
              page === '...' ? (
                <PaginationEllipsis key={`ellipsis-${idx}`}>…</PaginationEllipsis>
              ) : (
                <PaginationItem
                  key={page}
                  onClick={() => setPagination((old) => ({ ...old, pageIndex: page - 1 }))}
                >
                  <PaginationButton size="icon" isActive={page === currentPage}>
                    {page}
                  </PaginationButton>
                </PaginationItem>
              ),
            )}
          </PaginationContent>

          <PaginationItem>
            <PaginationNext onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              {t('next')}
            </PaginationNext>
          </PaginationItem>
        </Pagination>
      </div>
    </CardContent>
  );
};
